// components/analytics/sections/GamificationAnalytics.tsx
import { useState } from "react";
import { Row, Col, Card, Typography, Statistic, Tooltip, Table, Tag, Button } from "antd";
import {
  GiftOutlined, StarOutlined, PercentageOutlined, TagOutlined, RiseOutlined,
  InfoCircleOutlined, SafetyCertificateOutlined, DollarOutlined, TeamOutlined,
  UserOutlined, ThunderboltOutlined, SwapOutlined, TrophyOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  EyeOutlined, EyeInvisibleOutlined
} from "@ant-design/icons";
import { TrendLineChart } from "../components/charts/TrendLineChart";
import { PieChart } from "../components/charts/PieChart";
import { BarChart } from "../components/charts/BarChart";
import type { GamificationAnalytics as GamificationAnalyticsType } from "../../../types/analytics";
import { useTheme } from "../../../contexts/ThemeContext";

const { Text } = Typography;

interface GamificationAnalyticsProps {
  data?: GamificationAnalyticsType | null;
  loading?: boolean;
}

// Reusable ModernStatCard
const ModernStatCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  suffix?: string | React.ReactNode;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  info?: string;
  theme?: 'dark' | 'light';
}> = ({ title, value, icon, color, suffix, prefix, precision, loading, info, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' ? '#23272f' : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16, padding: '20px',
      boxShadow: theme === 'dark' ? '0 2px 8px rgba(0,0,0,0.32)' : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      border: theme === 'dark' ? '1px solid #23272f' : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative', overflow: 'hidden', height: '120px',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    <div style={{ position: 'absolute', top: 0, right: 0, width: 60, height: 60, background: theme === 'dark' ? `linear-gradient(135deg, ${color}33, #23272f00)` : `linear-gradient(135deg, ${color}20, ${color}10)`, borderRadius: '0 16px 0 60px' }} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <Text style={{ fontSize: 13, fontWeight: 600, color: theme === 'dark' ? '#d9d9d9' : '#595959' }}>{title}</Text>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {info && <Tooltip title={info}><InfoCircleOutlined style={{ color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', fontSize: 12 }} /></Tooltip>}
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${color}, ${color}dd)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, boxShadow: `0 2px 8px ${color}40` }}>{icon}</div>
      </div>
    </div>
    <div style={{ zIndex: 1 }}>
      <Statistic value={value} prefix={prefix} suffix={suffix} precision={precision} loading={loading} valueStyle={{ fontSize: 24, fontWeight: 700, color: theme === 'dark' ? '#fff' : '#262626' }} />
    </div>
  </div>
);

const InfoStatCard: React.FC<{
  title: string; value: number | string; info: string; icon: React.ReactNode; color: string;
  prefix?: string | React.ReactNode; suffix?: string | React.ReactNode; precision?: number; loading?: boolean; theme?: 'dark' | 'light';
}> = ({ title, value, info, icon, color, prefix, suffix, precision, loading, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' ? '#23272f' : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16, padding: '20px',
      boxShadow: theme === 'dark' ? '0 2px 8px rgba(0,0,0,0.32)' : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      border: theme === 'dark' ? '1px solid #23272f' : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative', overflow: 'hidden', height: '120px',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    <div style={{ position: 'absolute', top: 0, right: 0, width: 60, height: 60, background: theme === 'dark' ? `linear-gradient(135deg, ${color}33, #23272f00)` : `linear-gradient(135deg, ${color}20, ${color}10)`, borderRadius: '0 16px 0 60px' }} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 13, fontWeight: 600, color: theme === 'dark' ? '#d9d9d9' : '#595959' }}>{title}</Text>
        <Tooltip title={info}><InfoCircleOutlined style={{ color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', fontSize: 12 }} /></Tooltip>
      </div>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${color}, ${color}dd)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, boxShadow: `0 2px 8px ${color}40` }}>{icon}</div>
    </div>
    <div style={{ zIndex: 1 }}>
      <Statistic value={value} prefix={prefix} suffix={suffix} precision={precision} loading={loading} valueStyle={{ fontSize: 24, fontWeight: 700, color: theme === 'dark' ? '#fff' : '#262626' }} />
    </div>
  </div>
);

const cardStyle = (theme: string) => ({
  background: theme === 'dark' ? '#23272f' : 'rgba(255, 255, 255, 0.95)',
  borderRadius: 16,
  boxShadow: theme === 'dark' ? '0 2px 8px rgba(0,0,0,0.32)' : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
  border: theme === 'dark' ? '1px solid #23272f' : '1px solid rgba(0, 0, 0, 0.06)',
});

export const GamificationAnalytics: React.FC<GamificationAnalyticsProps> = ({ data, loading = false }) => {
  const { theme } = useTheme();
  const [showSuspicious, setShowSuspicious] = useState(false);

  const bt = data?.bonusTotals;
  const ro = data?.rollover;
  const al = data?.antiLavado;
  const ri = data?.revenueImpact;
  const ue = data?.userEngagement;
  const rf = data?.referrals;
  const pe = data?.pointsEconomy;

  return (
    <div>
      {/* 1. Bonus Overview */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={5}>
          <ModernStatCard title="Total Bonus ($)" value={bt?.totalBonusGiven ?? 0} icon={<GiftOutlined />} color="#722ed1" prefix="$" precision={2} loading={loading} info="Dinero otorgado en bonus (PERCENTAGE + FIXED_AMOUNT)" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <ModernStatCard title="Total Puntos" value={bt?.totalPointsGiven ?? 0} icon={<StarOutlined />} color="#faad14" precision={0} loading={loading} info="Puntos otorgados por reglas tipo POINTS" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <InfoStatCard title="Bonus Aplicados" value={bt?.bonusApplicationCount ?? 0} icon={<GiftOutlined />} color="#eb2f96" loading={loading} info="Cantidad total de aplicaciones de bonus" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <InfoStatCard title="Costo vs Ingresos" value={`${(bt?.bonusCostPercentage ?? 0).toFixed(2)}%`} icon={<PercentageOutlined />} color="#ff4d4f" loading={loading} info="Porcentaje de bonus en dinero sobre ingresos totales" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Promedio ($)" value={`$${(bt?.averageBonusPerApplication ?? 0).toFixed(2)}`} icon={<GiftOutlined />} color="#fa8c16" loading={loading} info="Monto promedio de bonus en dinero por aplicación" theme={theme} />
        </Col>
      </Row>

      {/* 2. Top Reglas de Bonus */}
      {bt?.topRules && bt.topRules.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TagOutlined style={{ color: '#722ed1' }} /> Top Reglas de Bonus</div>} loading={loading} style={cardStyle(theme)}>
              <Table
                dataSource={bt.topRules.map((rule, idx) => ({ ...rule, key: idx, rank: idx + 1 }))}
                columns={[
                  { title: '#', dataIndex: 'rank', key: 'rank', width: 50 },
                  { title: 'Regla', dataIndex: 'ruleName', key: 'ruleName' },
                  { title: 'Tipo', dataIndex: 'bonusType', key: 'bonusType', width: 120, render: (v: string) => v === 'POINTS' ? 'Puntos' : v === 'PERCENTAGE' ? 'Porcentaje' : 'Monto Fijo' },
                  { title: 'Total', dataIndex: 'totalAmount', key: 'totalAmount', render: (v: number, r: any) => r.bonusType === 'POINTS' ? `${v.toFixed(0)} pts` : `$${v.toFixed(2)}`, align: 'right' as const },
                  { title: 'Aplicaciones', dataIndex: 'count', key: 'count', align: 'right' as const },
                  { title: 'Promedio', dataIndex: 'avgAmount', key: 'avgAmount', render: (v: number, r: any) => r.bonusType === 'POINTS' ? `${v.toFixed(0)} pts` : `$${v.toFixed(2)}`, align: 'right' as const },
                  { title: '% del Total', dataIndex: 'percentage', key: 'percentage', render: (v: number) => `${v.toFixed(1)}%`, align: 'right' as const },
                ]}
                pagination={false} size="small"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 3. Desglose por Tipo + Tendencia de Bonus */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={8}>
          <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><StarOutlined style={{ color: '#1890ff' }} /> Desglose por Tipo</div>} loading={loading} style={cardStyle(theme)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Porcentaje', data: bt?.byType?.percentage, color: '#1890ff', unit: '$' },
                { label: 'Monto Fijo', data: bt?.byType?.fixedAmount, color: '#52c41a', unit: '$' },
                { label: 'Puntos', data: bt?.byType?.points, color: '#faad14', unit: 'pts' },
              ].map(({ label, data: typeData, color, unit }) => (
                <div key={label} style={{ background: theme === 'dark' ? '#181a20' : '#fafafa', borderRadius: 10, padding: '12px 16px', borderLeft: `4px solid ${color}` }}>
                  <Text style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>{label}</Text>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <Text style={{ fontSize: 16, fontWeight: 700, color: theme === 'dark' ? '#fff' : '#262626' }}>
                      {unit === '$' ? `$${(typeData?.amount ?? 0).toFixed(2)}` : `${(typeData?.amount ?? 0).toFixed(0)} pts`}
                    </Text>
                    <Text style={{ fontSize: 13, color: theme === 'dark' ? '#d9d9d9' : '#595959' }}>{typeData?.count ?? 0} aplicaciones</Text>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><RiseOutlined style={{ color: '#722ed1' }} /> Tendencia de Bonus</div>} loading={loading} style={cardStyle(theme)}>
            {bt?.bonusTrend && bt.bonusTrend.length > 0 ? (
              <TrendLineChart
                data={bt.bonusTrend.flatMap(item => [
                  { date: item.date, value: item.amount, type: 'Bonus ($)' },
                  ...(item.points > 0 ? [{ date: item.date, value: item.points, type: 'Puntos' }] : []),
                ])}
                xField="date" yField="value" seriesField="type"
              />
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>Sin datos de tendencia</div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 4. Rollover Analytics */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={5}>
          <ModernStatCard title="Rollovers Activos" value={ro?.activeCount ?? 0} icon={<ClockCircleOutlined />} color="#1890ff" loading={loading} info="Rollovers actualmente en curso" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <ModernStatCard title="Completados" value={ro?.completedCount ?? 0} icon={<CheckCircleOutlined />} color="#52c41a" loading={loading} info="Rollovers cumplidos exitosamente" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <ModernStatCard title="Cancelados" value={ro?.cancelledCount ?? 0} icon={<CloseCircleOutlined />} color="#f5222d" loading={loading} info="Rollovers cancelados" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <InfoStatCard title="Tasa Cumplimiento" value={`${(ro?.fulfillmentRate ?? 0).toFixed(1)}%`} icon={<CheckCircleOutlined />} color="#52c41a" loading={loading} info="Porcentaje de rollovers completados vs total" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Tiempo Prom. (hs)" value={(ro?.avgFulfillmentTimeHours ?? 0).toFixed(1)} icon={<ClockCircleOutlined />} color="#fa8c16" loading={loading} info="Horas promedio para completar un rollover" theme={theme} />
        </Col>
      </Row>

      {/* 5. Rollover Trends + Cancel Reasons */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><RiseOutlined style={{ color: '#1890ff' }} /> Tendencia de Rollovers</div>} loading={loading} style={cardStyle(theme)}>
            {ro?.rolloverTrend && ro.rolloverTrend.length > 0 ? (
              <TrendLineChart
                data={ro.rolloverTrend.flatMap(item => [
                  { date: item.date, value: item.activated, type: 'Activados' },
                  { date: item.date, value: item.completed, type: 'Completados' },
                  { date: item.date, value: item.cancelled, type: 'Cancelados' },
                ])}
                xField="date" yField="value" seriesField="type"
              />
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>Sin datos de tendencia</div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><CloseCircleOutlined style={{ color: '#f5222d' }} /> Razones de Cancelación</div>} loading={loading} style={cardStyle(theme)}>
            {ro?.cancelReasonBreakdown && ro.cancelReasonBreakdown.length > 0 ? (
              <PieChart
                data={ro.cancelReasonBreakdown.map(r => ({ name: r.reason, value: r.count }))}
                nameField="name" valueField="value" height={300}
              />
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>Sin datos de cancelación</div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 6. Anti-Lavado Compliance */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard title="Compliance Rate" value={`${(al?.complianceRate ?? 100).toFixed(1)}%`} icon={<SafetyCertificateOutlined />} color="#52c41a" loading={loading} info="Tasa de cumplimiento de rollover" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Rollovers Evaluados" value={al?.totalRolloversEvaluated ?? 0} icon={<SafetyCertificateOutlined />} color="#1890ff" loading={loading} info="Total de rollovers evaluados en el período" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Uso Camino B" value={`${(al?.caminoBUsageRate ?? 0).toFixed(1)}%`} icon={<SwapOutlined />} color="#fa8c16" loading={loading} info={`Tasa de uso de Camino B (${al?.caminoBCount ?? 0} casos)`} theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Usuarios Sospechosos" value={al?.suspiciousPatterns?.length ?? 0} icon={<UserOutlined />} color="#ff4d4f" loading={loading} info="Usuarios con ratio retiro/depósito sospechoso" theme={theme} />
        </Col>
      </Row>

      {/* Suspicious patterns table (toggle) */}
      {al?.suspiciousPatterns && al.suspiciousPatterns.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col xs={24}>
            <Button
              type="link"
              icon={showSuspicious ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setShowSuspicious(!showSuspicious)}
              style={{ padding: 0 }}
            >
              {showSuspicious ? 'Ocultar detalle' : 'Ver detalle de usuarios sospechosos'}
            </Button>
            {showSuspicious && (
              <Card style={{ ...cardStyle(theme), marginTop: 8 }}>
                <Table
                  dataSource={al.suspiciousPatterns.map((u, i) => ({ ...u, key: i }))}
                  columns={[
                    { title: 'Usuario', dataIndex: 'username', key: 'username' },
                    { title: 'Ratio Retiro/Depósito', dataIndex: 'withdrawToDepositRatio', key: 'ratio', render: (v: number) => v.toFixed(3), align: 'right' as const },
                    { title: 'Bonus Recibido', dataIndex: 'totalBonusReceived', key: 'bonus', render: (v: number) => `$${v.toFixed(2)}`, align: 'right' as const },
                    { title: 'Nivel', dataIndex: 'flagLevel', key: 'flag', render: (v: string) => (
                      <Tag color={v === 'high' ? 'red' : v === 'medium' ? 'orange' : 'green'}>{v.toUpperCase()}</Tag>
                    )},
                  ]}
                  pagination={false} size="small"
                />
              </Card>
            )}
          </Col>
        </Row>
      )}

      {/* 7. Revenue Impact */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={4}>
          <ModernStatCard title="ROI de Bonus" value={`${(ri?.bonusROI ?? 0).toFixed(1)}%`} icon={<DollarOutlined />} color="#52c41a" loading={loading} info="Retorno de inversión: (ingresos_extra - bonus) / bonus * 100" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Eficiencia" value={`${(ri?.bonusEfficiency ?? 0).toFixed(1)}x`} icon={<ThunderboltOutlined />} color="#1890ff" loading={loading} info="Depósitos de recipients / bonus otorgado" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Mejora Retención" value={`${(ri?.retentionUplift ?? 0).toFixed(1)}%`} icon={<RiseOutlined />} color="#722ed1" loading={loading} info="% más depósitos de usuarios con bonus vs sin bonus" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Costo/Usuario" value={`$${(ri?.costPerRetainedUser ?? 0).toFixed(2)}`} icon={<UserOutlined />} color="#fa8c16" loading={loading} info="Costo promedio de bonus por usuario retenido" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Avg con Bonus" value={`$${(ri?.avgDepositsWithBonus ?? 0).toFixed(0)}`} icon={<DollarOutlined />} color="#13c2c2" loading={loading} info="Promedio de depósitos de usuarios con bonus" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Avg sin Bonus" value={`$${(ri?.avgDepositsWithoutBonus ?? 0).toFixed(0)}`} icon={<DollarOutlined />} color="#8c8c8c" loading={loading} info="Promedio de depósitos de usuarios sin bonus" theme={theme} />
        </Col>
      </Row>

      {/* 8. User Engagement */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard title="Total Gamificados" value={ue?.totalGamifiedUsers ?? 0} icon={<TrophyOutlined />} color="#722ed1" loading={loading} info="Usuarios con perfil de gamificación" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Avg Racha" value={(ue?.avgStreakLength ?? 0).toFixed(1)} icon={<ThunderboltOutlined />} color="#fa8c16" loading={loading} info="Longitud promedio de racha actual" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Streak Break Rate" value={`${(ue?.streakBreakRate ?? 0).toFixed(1)}%`} icon={<CloseCircleOutlined />} color="#f5222d" loading={loading} info="Porcentaje de usuarios que rompieron su racha" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Progresión Nivel" value={`${(ue?.levelProgressionRate ?? 0).toFixed(1)}%`} icon={<RiseOutlined />} color="#52c41a" loading={loading} info="% de usuarios que superaron nivel 1" theme={theme} />
        </Col>
      </Row>

      {/* Level Distribution Chart */}
      {ue?.levelDistribution && ue.levelDistribution.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TrophyOutlined style={{ color: '#722ed1' }} /> Distribución de Niveles</div>} loading={loading} style={cardStyle(theme)}>
              <BarChart
                data={ue.levelDistribution.map(l => ({ name: l.levelName, value: l.count }))}
                xField="name" yField="value" height={300}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 9. Referidos */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard title="Total Referidos" value={rf?.totalReferrals ?? 0} icon={<TeamOutlined />} color="#1890ff" loading={loading} info="Total de referidos en el período" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Referidores Activos" value={rf?.activeReferrers ?? 0} icon={<UserOutlined />} color="#52c41a" loading={loading} info="Usuarios únicos que refirieron a alguien" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Tasa Conversión" value={`${(rf?.referralConversionRate ?? 0).toFixed(1)}%`} icon={<CheckCircleOutlined />} color="#722ed1" loading={loading} info="% de referidos que hicieron al menos 1 carga" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard title="Avg Bonus/Referido" value={`$${(rf?.avgBonusPerReferral ?? 0).toFixed(2)}`} icon={<GiftOutlined />} color="#fa8c16" loading={loading} info="Bonus promedio generado por referido" theme={theme} />
        </Col>
      </Row>

      {/* Top Referrers + Trend */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {rf?.topReferrers && rf.topReferrers.length > 0 && (
          <Col xs={24} lg={10}>
            <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TeamOutlined style={{ color: '#1890ff' }} /> Top Referidores</div>} loading={loading} style={cardStyle(theme)}>
              <Table
                dataSource={rf.topReferrers.map((r, i) => ({ ...r, key: i, rank: i + 1 }))}
                columns={[
                  { title: '#', dataIndex: 'rank', key: 'rank', width: 50 },
                  { title: 'Usuario', dataIndex: 'username', key: 'username' },
                  { title: 'Referidos', dataIndex: 'referralCount', key: 'count', align: 'right' as const },
                  { title: 'Bonus Total', dataIndex: 'totalBonus', key: 'bonus', render: (v: number) => `$${v.toFixed(2)}`, align: 'right' as const },
                ]}
                pagination={false} size="small"
              />
            </Card>
          </Col>
        )}
        <Col xs={24} lg={rf?.topReferrers?.length ? 14 : 24}>
          <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><RiseOutlined style={{ color: '#1890ff' }} /> Tendencia de Referidos</div>} loading={loading} style={cardStyle(theme)}>
            {rf?.referralTrend && rf.referralTrend.length > 0 ? (
              <TrendLineChart
                data={rf.referralTrend.flatMap(item => [
                  { date: item.date, value: item.count, type: 'Referidos' },
                  { date: item.date, value: item.conversions, type: 'Conversiones' },
                ])}
                xField="date" yField="value" seriesField="type"
              />
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>Sin datos de tendencia</div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 10. Economía de Puntos */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={4}>
          <ModernStatCard title="Ganados" value={pe?.totalEarned ?? 0} icon={<StarOutlined />} color="#52c41a" precision={0} loading={loading} info="Total de puntos ganados en el período" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <ModernStatCard title="Canjeados" value={pe?.totalRedeemed ?? 0} icon={<StarOutlined />} color="#f5222d" precision={0} loading={loading} info="Total de puntos canjeados en el período" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Tasa Canje" value={`${(pe?.redemptionRate ?? 0).toFixed(1)}%`} icon={<PercentageOutlined />} color="#722ed1" loading={loading} info="Porcentaje de puntos canjeados vs ganados" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Velocidad/día" value={(pe?.pointsVelocity ?? 0).toFixed(1)} icon={<ThunderboltOutlined />} color="#1890ff" loading={loading} info="Puntos ganados por día en el período" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Inflación" value={(pe?.inflationIndex ?? 0).toFixed(3)} icon={<RiseOutlined />} color="#fa8c16" loading={loading} info="Outstanding / total ganado lifetime (menor = más sano)" theme={theme} />
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard title="Outstanding" value={pe?.currentOutstanding ?? 0} icon={<StarOutlined />} color="#13c2c2" precision={0} loading={loading} info="Puntos actualmente en circulación" theme={theme} />
        </Col>
      </Row>

      {/* Points Trend */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><StarOutlined style={{ color: '#faad14' }} /> Tendencia de Puntos</div>} loading={loading} style={cardStyle(theme)}>
            {pe?.earnedTrend && pe.earnedTrend.length > 0 ? (
              <TrendLineChart
                data={pe.earnedTrend.flatMap(item => [
                  { date: item.date, value: item.earned, type: 'Ganados' },
                  { date: item.date, value: item.redeemed, type: 'Canjeados' },
                ])}
                xField="date" yField="value" seriesField="type"
              />
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>Sin datos de tendencia de puntos</div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
